/**
 * Utility functions for consistent data formatting throughout the application
 */

/**
 * Format a number as currency (AUD)
 * 
 * @param {number} value - The value to format
 * @param {Object} options - Formatting options
 * @returns {string} - Formatted currency string
 */
export const formatCurrency = (value, options = {}) => {
  const {
    locale = 'en-AU',
    currency = 'AUD',
    minimumFractionDigits = 0,
    maximumFractionDigits = 0,
    showSymbol = true,
    abbreviate = false
  } = options;
  
  // Handle non-numeric values
  if (value === null || value === undefined || isNaN(value)) {
    return 'N/A';
  }
  
  // If abbreviation is requested, convert to K, M, B format
  if (abbreviate) {
    return abbreviateNumber(value, {
      ...options,
      prefix: showSymbol ? '$' : '',
    });
  }
  
  // Format using Intl.NumberFormat
  try {
    const formatter = new Intl.NumberFormat(locale, {
      style: showSymbol ? 'currency' : 'decimal',
      currency: showSymbol ? currency : undefined,
      minimumFractionDigits,
      maximumFractionDigits
    });
    
    return formatter.format(value);
  } catch (error) {
    console.error('Error formatting currency:', error);
    
    // Fallback formatting
    const formatted = showSymbol ? `$${value.toFixed(minimumFractionDigits)}` : value.toFixed(minimumFractionDigits);
    return formatted;
  }
};

/**
 * Abbreviate large numbers to K (thousands), M (millions), B (billions)
 * 
 * @param {number} value - The value to abbreviate
 * @param {Object} options - Formatting options
 * @returns {string} - Abbreviated number string
 */
export const abbreviateNumber = (value, options = {}) => {
  const { 
    decimals = 1,
    prefix = '', 
    suffix = '' 
  } = options;
  
  // Handle non-numeric values
  if (value === null || value === undefined || isNaN(value)) {
    return 'N/A';
  }
  
  // Convert to absolute value for abbreviation
  const absValue = Math.abs(value);
  let abbreviatedValue;
  let unit = '';
  
  if (absValue >= 1000000000) {
    abbreviatedValue = value / 1000000000;
    unit = 'B';
  } else if (absValue >= 1000000) {
    abbreviatedValue = value / 1000000;
    unit = 'M';
  } else if (absValue >= 1000) {
    abbreviatedValue = value / 1000;
    unit = 'K';
  } else {
    abbreviatedValue = value;
  }
  
  // Format with fixed decimals
  if (decimals === 0) {
    return `${prefix}${Math.round(abbreviatedValue)}${unit}${suffix}`;
  } else {
    // Only show decimals if needed
    const rounded = Math.round(abbreviatedValue * Math.pow(10, decimals)) / Math.pow(10, decimals);
    const hasDecimal = rounded % 1 !== 0;
    
    if (hasDecimal) {
      return `${prefix}${rounded.toFixed(decimals)}${unit}${suffix}`;
    } else {
      return `${prefix}${Math.round(abbreviatedValue)}${unit}${suffix}`;
    }
  }
};

/**
 * Format a date string into a more readable format
 * 
 * @param {string|Date} dateValue - The date to format
 * @param {Object} options - Formatting options
 * @returns {string} - Formatted date string
 */
export const formatDate = (dateValue, options = {}) => {
  const {
    locale = 'en-AU',
    format = 'medium', // 'short', 'medium', 'long', 'full', or object with custom options
    timeZone = 'Australia/Sydney'
  } = options;
  
  // Handle invalid dates
  if (!dateValue) {
    return 'N/A';
  }
  
  try {
    // Create a date object from the input
    const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
    
    // Check if the date is valid
    if (isNaN(date.getTime())) {
      return 'Invalid Date';
    }
    
    // Set up the formatter options
    let formatterOptions;
    
    if (typeof format === 'object') {
      // Use custom format options
      formatterOptions = format;
    } else {
      // Use predefined formats
      switch (format) {
        case 'short':
          formatterOptions = { 
            day: 'numeric', 
            month: 'short', 
            year: 'numeric' 
          };
          break;
        case 'medium':
          formatterOptions = { 
            day: 'numeric', 
            month: 'long', 
            year: 'numeric' 
          };
          break;
        case 'long':
          formatterOptions = { 
            weekday: 'long',
            day: 'numeric', 
            month: 'long', 
            year: 'numeric'
          };
          break;
        case 'full':
          formatterOptions = { 
            weekday: 'long',
            day: 'numeric', 
            month: 'long', 
            year: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            timeZoneName: 'short'
          };
          break;
        case 'year':
          formatterOptions = { year: 'numeric' };
          break;
        case 'month':
          formatterOptions = { month: 'long' };
          break;
        case 'monthYear':
          formatterOptions = { month: 'long', year: 'numeric' };
          break;
        default:
          formatterOptions = { 
            day: 'numeric', 
            month: 'long', 
            year: 'numeric' 
          };
      }
    }
    
    // Add timeZone if needed
    if (timeZone) {
      formatterOptions.timeZone = timeZone;
    }
    
    // Format the date using Intl.DateTimeFormat
    const formatter = new Intl.DateTimeFormat(locale, formatterOptions);
    return formatter.format(date);
  } catch (error) {
    console.error('Error formatting date:', error);
    
    // Return the original date value as fallback
    return String(dateValue);
  }
};

/**
 * Format a percentage value
 * 
 * @param {number} value - The value to format as percentage
 * @param {Object} options - Formatting options
 * @returns {string} - Formatted percentage string
 */
export const formatPercentage = (value, options = {}) => {
  const {
    decimals = 2,
    includeSymbol = true,
    locale = 'en-AU',
    minimumValue = 0.01
  } = options;
  
  // Handle non-numeric values
  if (value === null || value === undefined || isNaN(value)) {
    return 'N/A';
  }
  
  // Convert from decimal to percentage if needed (0.1 -> 10%)
  const percentValue = value > 1 ? value : value * 100;
  
  // Set minimum display value if very small but not zero
  const displayValue = (percentValue !== 0 && Math.abs(percentValue) < minimumValue) 
    ? (percentValue < 0 ? -minimumValue : minimumValue)
    : percentValue;
    
  try {
    if (includeSymbol) {
      const formatter = new Intl.NumberFormat(locale, {
        style: 'percent',
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
      });
      
      // NumberFormat expects decimal (0.1) for percentage
      return formatter.format(displayValue / 100);
    } else {
      return displayValue.toFixed(decimals);
    }
  } catch (error) {
    console.error('Error formatting percentage:', error);
    
    // Fallback formatting
    const formatted = includeSymbol 
      ? `${displayValue.toFixed(decimals)}%` 
      : displayValue.toFixed(decimals);
    
    return formatted;
  }
};

/**
 * Format large numbers with thousands separators
 * 
 * @param {number} value - The value to format
 * @param {Object} options - Formatting options
 * @returns {string} - Formatted number string
 */
export const formatNumber = (value, options = {}) => {
  const {
    locale = 'en-AU',
    decimals = 0,
    abbreviate = false,
    prefix = '',
    suffix = ''
  } = options;
  
  // Handle non-numeric values
  if (value === null || value === undefined || isNaN(value)) {
    return 'N/A';
  }
  
  // If abbreviation is requested, convert to K, M, B format
  if (abbreviate) {
    return abbreviateNumber(value, { 
      decimals, 
      prefix, 
      suffix 
    });
  }
  
  try {
    const formatter = new Intl.NumberFormat(locale, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });
    
    return `${prefix}${formatter.format(value)}${suffix}`;
  } catch (error) {
    console.error('Error formatting number:', error);
    
    // Fallback formatting
    return `${prefix}${value.toFixed(decimals)}${suffix}`;
  }
};

/**
 * Calculate percentage change between two values
 * 
 * @param {number} currentValue - The current value
 * @param {number} previousValue - The previous value to compare against
 * @param {Object} options - Formatting options
 * @returns {string} - Formatted percentage change
 */
export const formatChange = (currentValue, previousValue, options = {}) => {
  const {
    formatAsPercentage = true,
    decimals = 1,
    includeSymbol = true,
    includeSign = true
  } = options;
  
  // Handle non-numeric or zero previous value
  if (previousValue === 0 || 
      previousValue === null || 
      previousValue === undefined || 
      isNaN(previousValue) ||
      currentValue === null || 
      currentValue === undefined || 
      isNaN(currentValue)) {
    return 'N/A';
  }
  
  // Calculate percentage change
  const change = (currentValue - previousValue) / Math.abs(previousValue);
  
  // Format the change
  if (formatAsPercentage) {
    return formatPercentage(change, {
      decimals,
      includeSymbol,
      minimumValue: 0.1 // Minimum 0.1% change to display
    });
  } else {
    // Format as regular number
    let formatted = formatNumber(change * 100, {
      decimals
    });
    
    // Add sign if requested
    if (includeSign && change > 0) {
      formatted = `+${formatted}`;
    }
    
    // Add percentage symbol if requested
    if (includeSymbol) {
      formatted = `${formatted}%`;
    }
    
    return formatted;
  }
};

/**
 * Convert a string to title case
 * 
 * @param {string} text - Text to convert
 * @returns {string} - Title case text
 */
export const toTitleCase = (text) => {
  if (!text) return '';
  
  return text
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/**
 * Format a file size in bytes to a human-readable string
 * 
 * @param {number} bytes - Size in bytes
 * @param {Object} options - Formatting options
 * @returns {string} - Formatted file size
 */
export const formatFileSize = (bytes, options = {}) => {
  const {
    decimals = 2,
    spaceBetween = true,
    locale = 'en-AU'
  } = options;
  
  if (bytes === 0) return `0${spaceBetween ? ' ' : ''}B`;
  
  if (isNaN(bytes) || bytes < 0) return 'Invalid size';
  
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  
  // Format with locale-specific thousands separators
  const formatter = new Intl.NumberFormat(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
  
  const value = bytes / Math.pow(1024, i);
  return `${formatter.format(value)}${spaceBetween ? ' ' : ''}${sizes[i]}`;
};

export default {
  formatCurrency,
  formatNumber,
  formatPercentage,
  formatDate,
  formatChange,
  abbreviateNumber,
  toTitleCase,
  formatFileSize
}; 